import {
  ArrowUpDown,
  Building2,
  List as ListIcon,
  Newspaper,
  PanelsTopLeft,
  ChevronRight,
  FileBarChart,
  FileText,
  Filter,
  Link2,
  RefreshCw,
  RotateCcw,
  Trash2,
  Search,
  ListChecks,
  Sparkles,
  Calculator,
  SquarePen,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  apiClient,
  type UploadSummary
} from "@/shared/api/api-client";
import {
  NO_VALUE,
  criteriaFromState,
  onlyUnclassified,
} from "@/features/mentions/mention-filter-criteria";
import { uploadLabel } from "@/features/uploads/upload-shared";
import {
  LoadFilterDialog,
  SaveFilterDialog,
  SavedFiltersButton,
} from "@/features/mentions/SavedFilterDialogs";
import { useShowsCena } from "@/shared/components/cena-visibility";
import { usePolarityScale } from "@/shared/components/polarity-choices";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import {
  type ReportRecorte,
} from "@/features/reports/report-recorte";
import {
  publisherSuffix,
  influencerTier,
  toMention,
  toPublication,
  type Mention,
  type Publication,
} from "@/shared/types/mention";
import { mentionPlatform } from "@/shared/components/mention-platform";
import { hasPlatformLogo, PlatformLogo } from "@/shared/components/PlatformLogo";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import { SummaryPeriodPicker } from "@/shared/components/SummaryPeriodPicker";
import { MentionReviewMode } from "./MentionReviewMode";
import { useMentionTextView } from "./use-mention-text-view";
import {
  MENTION_TEXT_VIEWS,
  MENTION_TEXT_VIEW_LABELS,
  type MentionTextView,
} from "@/shared/vocabulary/mention-text-views";
import { MentionCard } from "./MentionCard";
import { parseReadingMode, type ReadingMode } from "./reading-mode";
import { selectionTarget } from "./selection-target";
import {
  replaceMentionInPlace,
  replacePublicationBrandInPlace,
} from "./mention-in-place";
import type { MentionCatalog } from "./MentionInlineCatalog";
import type { OpenMentionOptions } from "@/shell/app-shell-context";
import {
  EngagementMetricsList,
  PublicationTags,
  PublicationTitle,
  PressReachLine,
  ReachOrValue,
  engagementMetrics,
} from "./mention-parts";

import { useDismiss } from "@/shared/hooks/useDismiss";
import { usePolling } from "@/shared/hooks/usePolling";
import { Pager } from "@/shared/components/Pager";
import { ApiState } from "@/shared/components/ApiState";
import { ApiError, INVALID_SEARCH_CODE } from "@/shared/api/http";
import type {
  AiReprocessItem,
  AiReprocessRun,
  BulkEdit,
  BulkEditFailure,
  BulkPatchWire,
  CenaRecalcRun,
  PostSource,
} from "@/shared/api/api-client";
import { BulkClassificationDialog } from "./BulkClassificationDialog";
import { AiReprocessDialog } from "./AiReprocessDialog";
import { AiReprocessBanner } from "./AiReprocessBanner";
import { CenaRecalcDialog } from "./CenaRecalcDialog";
import { CenaRecalcBanner } from "./CenaRecalcBanner";
import { isAiReprocessTracked } from "./ai-reprocess-view";
import { LinkMentionDialog } from "./LinkMentionDialog";
import { Toast } from "@/shared/components/Toast";
import { errorMessage } from "@/shared/api/http";
import {
  MENTION_ORDER_DEFAULT,
  MENTION_ORDER_LABELS,
  MENTION_ORDERS,
  TRASH_ORDERS,
  type MentionOrder
} from "@/shared/vocabulary/mention-orders";
import { BulkEditBanner, isBulkEditRunning } from "./BulkEditBanner";
import { useDiscardActions, discardDialogCopy } from "./use-discard-actions";
import { MentionFilterPanel } from "./MentionFilterPanel";
import { MentionQuickFilters } from "./MentionQuickFilters";
import {
  DEFAULT_QUICK_FILTERS,
  readQuickFilters,
  togglePinnedQuickFilter,
  writeQuickFilters,
  type QuickFilterKey,
} from "./quick-filters";
import { useMentionFilters, type MentionListScope } from "./use-mention-filters";
// Ponto de encaixe do Versa (módulo removível — ver versa/ARCHITECTURE.md).
import { SendToVersaButton } from "@/features/versa/mentions/SendToVersa";
import { SendToDashboardButton } from "@/features/dashboard/SendToDashboard";

/**
 * Menções — primeira tela de dados no contrato real do web_api.
 *
 * **Filtro e paginação são do SERVIDOR.** A versão anterior puxava
 * `limit=2000` e filtrava com `useMemo`+`slice` no browser: mentia no
 * total, não sobrevive a volume real, e o "de X menções" contava só o
 * que tinha vindo. Aqui cada mudança de filtro é uma requisição nova.
 *
 * **O que saiu nesta leva, e por quê:**
 * - **Reclassificar / excluir em lote**: reclassificação é a Camada 2.5
 *   (`ReclassificationDraft`, com rascunho→aprovação em `elt/`), que tem
 *   desenho próprio e ainda não foi construída no back. Exclusão de
 *   menção não existe no domínio novo — o vínculo é resultado da
 *   esteira, não dado editável.
 * - **Filtro por tema**: `PostTheme` é N:N com o catálogo e ainda não
 *   vem no payload (entra com o vertical de catálogos).
 * - **Presets de filtro**: o backend que os guardava foi descartado.
 * - **Filtro por veículo**: virou busca textual (`q` casa em canal
 *   também) até existir o catálogo de veículos pra listar as opções.
 */
/** As opções do seletor de "linhas por página". 25 é o default porque
 * é o que cabe na tela sem rolar no notebook do analista; 100 existe
 * pra quem está conferindo um upload inteiro contra a planilha. */
const MENTION_PAGE_SIZES = [25, 50, 100] as const;


type MentionsPageProps = {
  token: string;
  /** O logo da empresa aberta: é a "foto" do cliente no cartão de cada
   *  menção dela (a de concorrente fica com as iniciais). */
  companyLogoUrl?: string | null;
  openMention: (mention: Mention, options?: OpenMentionOptions) => void;
  /** Sobe quando o drawer de menção muda alguma coisa (ver a casca). */
  mentionChange?: number;
  /** Qual menção o drawer mudou na última subida de `mentionChange`:
   *  com id, só aquela linha se atualiza, no lugar (não some por sair do
   *  filtro); `null` (vincular/mover) refaz a página em silêncio. */
  mentionChangeId?: number | null;
  /** Filtro "só as menções destes uploads" — mora na URL
   *  (`?upload=7&upload=4`), então quem muda é a rota, não o estado
   *  local. É o que faz o recorte sobreviver ao F5 e ser
   *  compartilhável. Vazio = sem filtro. */
  uploadFilter?: number[];
  onChangeUploadFilter?: (uploadIds: number[]) => void;
  /** Leva o recorte da tela pra geração de relatório. Quem navega é a
   *  rota (é ela que tem `navigate` e a empresa da URL); esta tela só
   *  sabe DIZER qual é o recorte. Ausente = o botão não aparece. */
  onGenerateReport?: (recorte: ReportRecorte) => void;
  /** Abre o dashboard no rascunho que o "Enviar para o Dashboard"
   *  acabou de gerar. A rota navega; ausente = o botão não aparece. */
  onOpenDashboardDraft?: (draftId: number) => void;
  /** Administrador ou acima: mostra "Reprocessar com IA". O servidor é
   *  quem cobra (403); isto só decide o que a tela oferece. */
  canReprocess?: boolean;
  /** Só leitura — o Visualizador (2026-10-22): vê a lista, filtra e abre
   *  a menção, e não classifica, revisa, descarta, vincula, seleciona em
   *  lote, salva filtro nem fecha amostra. O servidor recusa tudo isso
   *  pra ele; a tela não oferece. */
  readOnly?: boolean;
  /** Especialista ou acima: mostra "Recalcular CENA". O servidor cobra
   *  (403); isto só decide o que a tela oferece. */
  canRecalcCena?: boolean;
  /** O recálculo que o sino mandou abrir (`?recalculo=<id>`). */
  recalcLink?: number | null;
  onConsumeRecalcLink?: () => void;
  /** A execução que o sino mandou abrir (`?reprocessamento=<id>` ou
   *  `?previa=<id>` — o `mode` dela decide faixa ou diálogo). */
  reprocessLink?: number | null;
  onConsumeReprocessLink?: () => void;
  /** Abre a execução na tela de Reprocessamentos. A rota navega. */
  onOpenReprocessRun?: (runId: number) => void;
};

const NO_UPLOADS: number[] = [];

/** "list" | "review" — o modo de leitura escolhido por último. */
const MENTIONS_READING_KEY = "reputaition.mentions_reading";

/** O teto de menções por pedido de vínculo manual — espelha
 *  `MAX_SOURCES` em `web_api/application/manual_link.py`. */
const LINK_MAX_SOURCES = 100;


export function MentionsPage({
  token,
  openMention,
  mentionChange = 0,
  mentionChangeId = null,
  uploadFilter = NO_UPLOADS,
  onChangeUploadFilter,
  onGenerateReport,
  onOpenDashboardDraft,
  canReprocess = false,
  readOnly = false,
  canRecalcCena = false,
  recalcLink = null,
  onConsumeRecalcLink,
  reprocessLink = null,
  onConsumeReprocessLink,
  companyLogoUrl = null,
  onOpenReprocessRun,
}: MentionsPageProps) {
  // Com o CENA oculto, o filtro por CENA some — e o portão vale também
  // na QUERY e no contador, não só no input: um valor que sobrasse no
  // estado seguiria filtrando a lista sem nenhum chip visível contando.
  const showsCena = useShowsCena();
  const polarityScale = usePolarityScale();
  // Uploads pro filtro de origem (a lista mora aqui porque o hook só
  // precisa do rótulo de cada um, pro chip).
  const [uploads, setUploads] = useState<UploadSummary[]>([]);
  const uploadLabelOf = useCallback(
    (id: number) => {
      const match = uploads.find((item) => item.id === id);
      return match ? uploadLabel(match) : undefined;
    },
    [uploads],
  );
  // O recorte (filtros, rascunho do painel, busca, escopo, ordem,
  // página, lista colada e a montagem da consulta) mora no hook — e o
  // filtro em si, na loja da casca, compartilhado com o relatório.
  const filtersHook = useMentionFilters({
    token,
    uploadFilter,
    onChangeUploadFilter,
    showsCena,
    polarityScale,
    uploadLabelOf,
  });
  const {
    query,
    setQuery,
    searchIn,
    setSearchIn,
    debouncedQuery,
    filters,
    clearFilters,
    activePeriod,
    choosePeriod,
    facet,
    resolveIds,
    waitingForCatalog,
    filtersOpen,
    openPanel,
    closePanel,
    activeFilterCount,
    scope,
    changeScope: changeScopeFilters,
    hasCompetitors,
    order,
    changeOrder: changeOrderFilters,
    sourceMode,
    changeSourceMode: changeSourceModeFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    refFilter,
    expirePastedReferences,
    buildQuery,
    queryKey,
    applySavedFilter,
    reportRecorte,
    activeChips,
  } = filtersHook;
  const [savingFilter, setSavingFilter] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  // Os filtros que a pessoa fixou na barra, na ordem dela
  // (`quick-filters.ts`): o alfinete do painel escolhe, a alça da barra
  // ordena.
  const [quickFilters, setQuickFilters] = useState<QuickFilterKey[]>(readQuickFilters);
  const changeQuickFilters = useCallback((next: QuickFilterKey[]) => {
    setQuickFilters(next);
    writeQuickFilters(next);
  }, []);
  // O pop-up do "Fechar amostra" (2026-10-27, o mesmo desenho do "Enviar
  // para o Dashboard"): confirma antes de sair da tela e lista o que do
  // filtro não vai junto — a nota mínima, por exemplo, sumia em silêncio.
  const [closingSample, setClosingSample] = useState(false);
  const [mentions, setMentions] = useState<Mention[]>([]);
  // `null` = calculando: com busca de texto a lista vem sem total (a
  // contagem custa segundos) e o número chega de `countMentions`.
  const [total, setTotal] = useState<number | null>(0);
  // A contagem falhou (não por cancelamento nem por expressão torta):
  // a tela diz "total indisponível" — a lista está certa e fica.
  const [totalError, setTotalError] = useState(false);
  // A contagem DESISTIU: com busca de texto o servidor conta por até 2 s
  // (2026-10-26), e termo comum estoura — "governo" na SECOM casa 269
  // mil e levaria 6 s e três núcleos. A lista está certa; o número não
  // vem, e a tela diz que a busca casa muitas menções.
  const [totalGaveUp, setTotalGaveUp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Recusa da expressão de busca (422 `invalid_search`): aviso junto da
  // caixa, separado de `error`, porque a lista que estava na tela FICA.
  const [searchError, setSearchError] = useState<string | null>(null);
  const [actionMenuId, setActionMenuId] = useState<number | null>(null);
  // "Lista" (padrão) ou "Revisão": a mesma página lida em três painéis
  // pra conferir uma a uma (2026-10-13). Só na visão por marca.
  // A escolha fica guardada no navegador (como a ordem da lista);
  // primeira visita abre em Lista.
  // Dois jeitos de ler: Lista e Revisão. Valor desconhecido no
  // armazenamento cai na Lista. (As justificativas têm página própria,
  // `/justificativas`.)
  const [readingMode, setReadingModeState] = useState<ReadingMode>(() => {
    try {
      return parseReadingMode(window.localStorage.getItem(MENTIONS_READING_KEY));
    } catch {
      return "list";
    }
  });
  const setReadingMode = (next: ReadingMode) => {
    setReadingModeState(next);
    try {
      window.localStorage.setItem(MENTIONS_READING_KEY, next);
    } catch {
      /* armazenamento bloqueado: a escolha vale só nesta visita */
    }
  };
  // O resto da página só pergunta "estou na Revisão?".
  // O Modo revisão é edição do começo ao fim (atalhos de polaridade,
  // abordagem, "R" revisa): quem só lê fica na lista.
  const reviewMode = readingMode === "review" && !readOnly;
  // Cards da lista abertos pela seta (2026-10-13): o "Mais informações"
  // dentro do próprio card, sem modal. Estado só de tela.
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  const toggleExpanded = (id: number) =>
    setExpandedIds(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [publications, setPublications] = useState<Publication[]>([]);
  // `null` no modo publicações: lá o servidor manda `has_next` em vez de
  // `total`, porque contar publicações custa ~45% da tela (ver
  // `web_api/.../routes/mentions.py::PublicationPageOut`).
  const [hasNext, setHasNext] = useState(false);

  // ── Edição em lote (2026-09-21) ──────────────────────────────────
  // A seleção é por id de MENÇÃO e sobrevive à troca de página; muda
  // de filtro e ela zera (ver o efeito abaixo). `selectAllFiltered` é
  // o modo "tudo que o filtro retorna": aí o alvo é o recorte, que o
  // servidor congela no clique — não a lista de ids da tela.
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  // O alvo do pop-up "Vincular a outra marca": as menções, e a marca da
  // origem quando é uma só (ela sai da lista de destinos).
  const [linkTarget, setLinkTarget] = useState<{
    mentionIds: number[];
    sourceBrandId?: number;
    /** A fonte, quando a origem é UMA menção já em mãos; sem ela, o
     *  diálogo pergunta ao servidor (a seleção atravessa páginas). */
    sources?: PostSource[];
  } | null>(null);
  // O lote em andamento (ou recém-terminado) — o banner acompanha e a
  // lista recarrega quando ele termina.
  const [activeBulk, setActiveBulk] = useState<BulkEdit | null>(null);
  const [bulkFailures, setBulkFailures] = useState<BulkEditFailure[] | null>(null);
  const [bulkPausedUntil, setBulkPausedUntil] = useState<number | null>(null);
  const [reprocessPausedUntil, setReprocessPausedUntil] = useState<
    number | null
  >(null);
  // O reprocessamento por IA (2026-10-05): o diálogo, a execução em
  // andamento (ou recém-terminada) e os pulados/erros sob demanda.
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [activeReprocess, setActiveReprocess] = useState<AiReprocessRun | null>(null);
  // A prévia reaberta pelo sino — abre o diálogo no recorte guardado.
  const [reopenedSample, setReopenedSample] = useState<AiReprocessRun | null>(null);
  const [reprocessIssues, setReprocessIssues] = useState<AiReprocessItem[] | null>(null);
  // O recálculo do CENA (2026-11-04): o diálogo e a execução em curso.
  const [recalcDialogOpen, setRecalcDialogOpen] = useState(false);
  const [activeRecalc, setActiveRecalc] = useState<CenaRecalcRun | null>(null);
  // O aviso passageiro do descarte, com o "desfazer".
  const [toast, setToast] = useState<{ message: string; undo?: () => void } | null>(null);
  // Postagem ou resumo da IA como texto do card: preferência da PESSOA
  // (2026-10-31), lida uma vez e gravada ao mudar. Declarado DEPOIS do
  // `setToast` que o aviso de falha usa — a lição do `catalogFor`
  // (2026-10-09): closure que lê um `const` declarado abaixo compila e
  // só cai em runtime, na zona morta.
  const { view: textView, change: changeTextView } = useMentionTextView(token, (message) =>
    setToast({ message }),
  );

  const changeSourceMode = (mode: "brand" | "publication") => {
    changeSourceModeFilters(mode);
    setActionMenuId(null);
  };

  const changeScope = (next: MentionListScope) => {
    setActionMenuId(null);
    // Outro escopo é outra lista: a seleção não vale mais.
    clearSelection();
    changeScopeFilters(next);
  };

  const changeOrder = (next: MentionOrder) => {
    changeOrderFilters(next);
    setActionMenuId(null);
  };

  // As ordens pela data do descarte só valem com "Só as descartadas"; ao
  // desligar o filtro com uma delas ativa, volta pra ordem padrão.
  useEffect(() => {
    if (!filters.onlyDiscarded && TRASH_ORDERS.includes(order)) {
      changeOrderFilters(MENTION_ORDER_DEFAULT);
    }
  }, [filters.onlyDiscarded, order, changeOrderFilters]);

  // Uploads pro filtro de origem. Falha em silêncio de propósito: sem
  // essa lista o filtro some, mas a tela de menções continua inteira.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .listUploads(token, { page: 1, size: 50 })
      .then((result) => {
        if (!cancelled) setUploads(result.items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  // `silent`: refaz a página sem trocar a lista pelo "Carregando…" —
  // é a recarga depois de uma edição, em que a pessoa está olhando pra
  // linha que acabou de mudar e não pode perder a rolagem.
  // **Descarte por CARGA** (2026-10-05): mudar um filtro fora da página
  // 1 dispara duas consultas (a do filtro novo na página antiga e a da
  // volta pra página 1), e sem isto vencia a que chegasse por ÚLTIMO —
  // a página 4 de um recorte que talvez nem tenha página 4, com o
  // rodapé dizendo 1. Só a carga mais recente escreve na tela.
  const loadRequestRef = useRef(0);
  // Consulta velha é CANCELADA, não só ignorada: o contador acima
  // impede a resposta antiga de escrever na tela, mas ela já foi
  // baixada — 85 kB na frente da resposta nova, medido no online com
  // 17 kB/s. O abort economiza a rede; o contador cobre o que o abort
  // não alcança (resposta que já chegou inteira antes do cancelamento).
  const abortRef = useRef<AbortController | null>(null);
  const load = useCallback(async (options?: { silent?: boolean }) => {
    // A lista colada vence no servidor (uma semana). Vencida, o filtro
    // devolveria ZERO sem dizer por quê — então a tela avisa e solta o
    // filtro, em vez de mostrar uma lista vazia muda.
    if (refFilter && Date.now() > refFilter.expiresAt) {
      expirePastedReferences();
      return;
    }
    const minhaCarga = ++loadRequestRef.current;
    const current = () => loadRequestRef.current === minhaCarga;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    if (!options?.silent) setLoading(true);
    setError(null);
    setTotalError(false);
    setTotalGaveUp(false);
    try {
      if (sourceMode === "publication") {
        const result = await apiClient.listPublications(
          token,
          buildQuery(),
          { signal: controller.signal },
        );
        if (!current()) return;
        setPublications(result.items.map(toPublication));
        setHasNext(result.has_next);
      } else {
        const query = buildQuery();
        // Busca de texto: a página aparece na hora e o total vem depois
        // (o `count(*)` de um termo comum custa segundos; a página, ms).
        const textSearch = Boolean(query.q);
        const result = await apiClient.listMentions(token, query, {
          signal: controller.signal,
          withTotal: textSearch ? false : undefined,
        });
        if (!current()) return;
        setMentions(result.items.map(toMention));
        if (result.total !== null) {
          setTotal(result.total);
        } else if (
          (query.page ?? 1) === 1 &&
          result.items.length < (query.size ?? 25)
        ) {
          // Página 1 que não encheu: o total é o que veio, de graça.
          setTotal(result.items.length);
        } else {
          // Recarga silenciosa (edição no drawer, polling) mantém o
          // número que estava na tela até o novo chegar — piscar
          // "calculando…" a cada correção seria ruído.
          if (!options?.silent) setTotal(null);
          // Mesmo controller e mesmo descarte por carga da lista: carga
          // nova cancela a contagem velha.
          apiClient
            .countMentions(token, query, { signal: controller.signal })
            .then((counted) => {
              if (!current()) return;
              setTotal(counted.total);
              setTotalGaveUp(counted.total === null);
            })
            .catch((failure) => {
              if ((failure as { name?: string } | null)?.name === "AbortError") return;
              if (!current()) return;
              if (failure instanceof ApiError && failure.code === INVALID_SEARCH_CODE) return;
              // Nunca `setError`: a lista está certa, só o total falhou.
              setTotalError(true);
            });
        }
      }
      setSearchError(null);
    } catch (failure) {
      // Cancelada por uma carga mais nova: não é erro, e `loading` é
      // da carga nova — mexer nele aqui apagaria o "Carregando…" dela.
      if ((failure as { name?: string } | null)?.name === "AbortError") return;
      if (!current()) return;
      if (failure instanceof ApiError && failure.code === INVALID_SEARCH_CODE) {
        // Quem está no meio de digitar `lula AND` não pode perder a lista
        // a cada letra: o aviso fica na caixa e a lista anterior segue.
        setSearchError(failure.message);
        return;
      }
      setError(
        failure instanceof Error
          ? failure.message
          : "Não foi possível carregar as menções.",
      );
    } finally {
      if (current()) setLoading(false);
    }
  }, [token, buildQuery, sourceMode, refFilter, expirePastedReferences]);

  // Mudou o recorte, a seleção deixa de fazer sentido — o que estava
  // marcado pode nem estar mais na lista.
  useEffect(() => {
    setSelected(new Set());
    setSelectAllFiltered(false);
  }, [debouncedQuery, filters, sourceMode, uploadFilter, refFilter]);

  // Um lote deixado rodando sobrevive ao F5: a tela retoma o
  // acompanhamento do mais recente ainda ativo.
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    apiClient
      .listBulkEdits(token)
      .then((running) => {
        if (!cancelled && running.length) setActiveBulk(running[0]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, readOnly]);

  // Retoma o acompanhamento de um reprocessamento depois de um F5.
  useEffect(() => {
    if (!canReprocess) return;
    let cancelled = false;
    apiClient
      .listAiReprocess(token, 1)
      .then((recent) => {
        if (!cancelled && recent.length && isAiReprocessTracked(recent[0])) {
          setActiveReprocess(recent[0]);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, canReprocess]);

  // Os links do sino. A execução abre na faixa (inclusive terminada —
  // é o resultado que a pessoa veio ver); a prévia reabre o diálogo. Quem
  // decide qual dos dois é o `mode` que vem do servidor, não o link.
  // Prévia anterior à coluna `target` não tem recorte pra reabrir, e
  // execução que sumiu (404) idem: o clique só deixa a pessoa na lista.
  useEffect(() => {
    const runId = reprocessLink;
    if (runId === null) return;
    // Quem não pode reprocessar (o aviso da empresa chega a todos) cai na
    // lista, que é o que mudou; o parâmetro sai da URL do mesmo jeito.
    if (!canReprocess) {
      onConsumeReprocessLink?.();
      return;
    }
    let cancelled = false;
    apiClient
      .getAiReprocess(token, runId)
      .then((run) => {
        if (cancelled) return;
        setReprocessIssues(null);
        if (run.mode === "sample" && run.target) setReopenedSample(run);
        else if (run.mode === "apply") setActiveReprocess(run);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) onConsumeReprocessLink?.();
      });
    return () => {
      cancelled = true;
    };
    // `onConsumeReprocessLink` é recriada a cada render da rota.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canReprocess, reprocessLink]);

  // O link do sino (?recalculo=<id>): abre a faixa daquela execução e
  // a rota tira o parâmetro. Quem não recalcula cai na lista.
  useEffect(() => {
    const runId = recalcLink;
    if (runId === null) return;
    if (!canRecalcCena) {
      onConsumeRecalcLink?.();
      return;
    }
    let cancelled = false;
    apiClient
      .getCenaRecalc(token, runId)
      .then((run) => {
        if (!cancelled) setActiveRecalc(run);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) onConsumeRecalcLink?.();
      });
    return () => {
      cancelled = true;
    };
    // onConsumeRecalcLink é recriada a cada render da rota.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, canRecalcCena, recalcLink]);

  const refreshReprocess = useCallback(async () => {
    if (!activeReprocess) return;
    try {
      const next = await apiClient.getAiReprocess(token, activeReprocess.id);
      setActiveReprocess(next);
      if (!isAiReprocessTracked(next)) void load();
    } catch (failure) {
      // 429 é aviso pra esperar o `Retry-After`, não pra tentar no próximo
      // tick; outra falha: a próxima volta do polling tenta de novo.
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setReprocessPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
    }
  }, [token, activeReprocess, load]);

  usePolling({
    enabled: activeReprocess !== null && isAiReprocessTracked(activeReprocess),
    onTick: () => void refreshReprocess(),
    signature: activeReprocess
      ? `${activeReprocess.id}:${activeReprocess.status}:${activeReprocess.counters.pending}`
      : undefined,
    pausedUntil: reprocessPausedUntil,
  });

  const showReprocessIssues = async () => {
    if (!activeReprocess) return;
    try {
      setReprocessIssues(
        await apiClient.listAiReprocessItems(token, activeReprocess.id, {
          outcomes: [
            "failed",
            "skipped_conflict",
            "skipped_reviewed",
            "skipped_discarded",
          ],
        })
      );
    } catch {
      setReprocessIssues([]);
    }
  };

  const refreshBulk = useCallback(async () => {
    if (!activeBulk) return;
    try {
      const next = await apiClient.getBulkEdit(token, activeBulk.id);
      setActiveBulk(next);
      // Terminou: a lista mostra a classificação nova.
      if (!isBulkEditRunning(next)) void load();
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setBulkPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
    }
  }, [token, activeBulk, load]);

  usePolling({
    enabled: activeBulk !== null && isBulkEditRunning(activeBulk),
    onTick: () => void refreshBulk(),
    signature: activeBulk
      ? `${activeBulk.id}:${activeBulk.status}:${activeBulk.counters.pending}`
      : undefined,
    pausedUntil: bulkPausedUntil,
  });

  const toggleSelect = (mentionId: number) => {
    if (selectAllFiltered) {
      // Desmarcar UMA no modo "todas do filtro" volta pra seleção
      // explícita: a página inteira menos esta.
      setSelectAllFiltered(false);
      setSelected(new Set(mentions.map((m) => m.id).filter((id) => id !== mentionId)));
      return;
    }
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(mentionId)) next.delete(mentionId);
      else next.add(mentionId);
      return next;
    });
  };

  const pageIds = mentions.map((m) => m.id);
  const allOnPageSelected =
    selectAllFiltered ||
    (pageIds.length > 0 && pageIds.every((id) => selected.has(id)));

  const togglePage = () => {
    if (allOnPageSelected) {
      setSelectAllFiltered(false);
      setSelected((current) => {
        const next = new Set(current);
        for (const id of pageIds) next.delete(id);
        return next;
      });
      return;
    }
    setSelected((current) => new Set([...current, ...pageIds]));
  };

  const clearSelection = () => {
    setSelected(new Set());
    setSelectAllFiltered(false);
  };

  // `null` = "todas do filtro" com o total ainda sendo calculado.
  const selectionCount: number | null = selectAllFiltered ? total : selected.size;

  // O alvo do lote: o recorte inteiro, ou os ids marcados COM o escopo
  // em que foram marcados — é o escopo que diz ao servidor que uma
  // menção de concorrente pode estar entre eles.
  const bulkTarget = () =>
    selectionTarget({ selectAllFiltered, selected, scope, buildQuery });

  const adoptBulkResult = (result: BulkEdit) => {
    setBulkDialogOpen(false);
    clearSelection();
    setBulkFailures(null);
    setActiveBulk(result);
    // Lote pequeno já volta terminado: recarrega agora. O grande é
    // acompanhado pelo polling, que recarrega ao fim.
    if (!isBulkEditRunning(result)) void load();
  };

  const submitBulk = async (patch: BulkPatchWire) => {
    adoptBulkResult(
      await apiClient.submitBulkClassification(token, bulkTarget(), patch),
    );
  };

  /**
   * Edição pontual (correção, revisão): atualiza SÓ aquela menção, no
   * lugar, sem refazer a consulta do filtro (2026-11-03). Quem filtrou
   * "sem subtema" e pôs o subtema não pode ver a linha sumir no meio do
   * trabalho; o recorte volta a valer no botão "Atualizar" (ou ao mudar
   * filtro/página). Ver `mention-in-place.ts`. Se o detalhe não vier
   * (menção movida, rede), cai na recarga silenciosa de antes.
   */
  const refreshMentionInPlace = useCallback(
    async (mentionId: number) => {
      try {
        const detail = toMention(await apiClient.mentionDetail(token, mentionId));
        setMentions((current) => replaceMentionInPlace(current, detail));
        setPublications((current) => replacePublicationBrandInPlace(current, detail));
      } catch {
        void load({ silent: true });
      }
    },
    [token, load],
  );

  // O drawer mudou uma menção: a linha por trás se atualiza sozinha — no
  // lugar quando a casca diz QUAL menção foi corrigida/revisada; sem id
  // (vincular/mover cria ou tira linha) a página recarrega em silêncio.
  // Pula a montagem — `mentionChange` nasce com o valor que a casca já
  // tinha.
  const seenMentionChange = useRef(mentionChange);
  useEffect(() => {
    if (seenMentionChange.current === mentionChange) return;
    seenMentionChange.current = mentionChange;
    if (mentionChangeId !== null) void refreshMentionInPlace(mentionChangeId);
    else void load({ silent: true });
    // `mentionChangeId` anda junto com o contador; é ele que dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionChange, load, refreshMentionInPlace]);

  /**
   * O catálogo da marca de cada menção, pro "+" do card (2026-10-18).
   * Guarda a PROMESSA por dono+marca, e não só o resultado: 25 cards
   * abrindo o "+" juntos disparam uma carga, não 25. Só roda quando
   * alguém abre um "+". Falha sai do cache pra a próxima abertura tentar
   * de novo. Menção de concorrente lê o catálogo DELE (o vínculo é achado
   * pela marca, como no editor); sem achar, vem vazio — oferecer os temas
   * da empresa o servidor recusaria. O grupo-tema é só o título: se a
   * lista falhar, os temas aparecem planos.
   *
   * Mensagem-chave é cadastrada por FONTE (`key_message.data_source`), e
   * a mesma mensagem costuma existir nas duas: oferecer a lista inteira
   * mostrava cada uma duas vezes, sem dizer qual era de imprensa
   * (2026-11-04, pedido de cliente). O "+" oferece só as da fonte da
   * menção — é a mesma lista que o motor de IA usa pra ela —, e por isso
   * a fonte entra na chave do cache. Uma já aplicada da outra fonte
   * continua aparecendo como chip e pode ser removida.
   */
  const catalogPromises = useRef(new Map<string, Promise<MentionCatalog>>());
  const loadCatalog = useCallback(
    (mention: Mention): Promise<MentionCatalog> => {
      const key = `${mention.isCompetitor ? "competitor" : "own"}:${mention.brandId}:${mention.mediaType}`;
      const cached = catalogPromises.current.get(key);
      if (cached) return cached;
      const promise = (async (): Promise<MentionCatalog> => {
        let api = apiClient;
        if (mention.isCompetitor) {
          const targets = await apiClient.linkTargets(token);
          const competitorId =
            targets.find((target) => target.brand_id === mention.brandId)?.competitor_id ?? null;
          if (competitorId === null) return { themes: [], keyMessages: [], spokespersons: [] };
          api = apiClient.onCompetitor(competitorId);
        }
        const [themes, keyMessages, spokespersons, groups] = await Promise.all([
          api.listThemes(token, { brandId: mention.brandId }),
          api.listKeyMessages(token, { brandId: mention.brandId }),
          api.listSpokespersons(token, { brandId: mention.brandId }),
          api.listThemeGroups(token).catch(() => []),
        ]);
        const groupNames = new Map(groups.map((group) => [group.id, group.name]));
        return {
          themes: themes.map((theme) => ({
            id: theme.id,
            name: theme.name,
            group: theme.theme_group_id ? (groupNames.get(theme.theme_group_id) ?? null) : null,
          })),
          keyMessages: keyMessages
            .filter((item) => item.data_source === mention.mediaType)
            .map((item) => ({ id: item.id, name: item.message })),
          spokespersons: spokespersons.map((item) => ({ id: item.id, name: item.name })),
        };
      })();
      catalogPromises.current.set(key, promise);
      promise.catch(() => catalogPromises.current.delete(key));
      return promise;
    },
    [token],
  );

  /** Marca (ou desmarca) como revisada na hora, pelo menu da linha. */
  const toggleReviewed = async (mention: Mention) => {
    try {
      await apiClient.setMentionReviewed(
        token,
        mention.id,
        mention.reviewedAt === null
      );
    } catch (failure) {
      setToast({
        message: errorMessage(failure, "Não foi possível mudar a revisão."),
      });
      return;
    }
    // No lugar: com "Só não revisadas" a linha não some ao marcar.
    await refreshMentionInPlace(mention.id);
  };

  /**
   * Descartar abre a confirmação "tem certeza?" (decisão da dona,
   * 2026-10-18, com a lixeira); restaurar é imediato. O toast com
   * "Desfazer" continua. A lógica mora em `use-discard-actions.ts`.
   */
  const discard = useDiscardActions({
    token,
    reload: () => void load(),
    notify: setToast,
    bulkTarget,
    onBulkSubmitted: adoptBulkResult,
  });

  const showBulkFailures = async () => {
    if (!activeBulk) return;
    try {
      setBulkFailures(await apiClient.listBulkEditFailures(token, activeBulk.id));
    } catch {
      setBulkFailures([]);
    }
  };

  /**
   * Abre o detalhe de UMA marca de uma publicação.
   *
   * No modo publicações a linha não é uma menção — cada marca citada é
   * a sua. O payload da lista traz só o resumo por marca (polaridade e
   * CENA), então o drawer precisa buscar a menção inteira pelo id.
   */
  const openBrandMention = useCallback(
    async (mentionId: number) => {
      try {
        // Só este caminho abre o drawer EDITÁVEL (2026-10-22): a visão
        // Publicações não tem card com os controles, então o detalhe é onde
        // a classificação se corrige. Os outros `openMention` da página
        // seguem só leitura, de propósito.
        openMention(toMention(await apiClient.mentionDetail(token, mentionId)), { loadCatalog });
      } catch (failure) {
        setError(
          failure instanceof Error
            ? failure.message
            : "Não foi possível abrir a menção.",
        );
      }
    },
    [token, openMention, loadCatalog],
  );

  // A consulta dispara pela CHAVE do recorte — o texto da query que vai
  // pro servidor —, e não pela identidade de `load`. `load` é recriada
  // sempre que `buildQuery` é, e `buildQuery` depende do catálogo de
  // marcas e das opções do painel: na abertura da tela, quando `/brands`
  // chegava (~1 s depois), `load` nascia de novo e o efeito pedia a
  // MESMA lista pela segunda vez, com a primeira ainda no ar. Medido no
  // online em 2026-10-07: dois `GET /mentions` de 85 kB por abertura,
  // numa rede de 17 kB/s — o segundo custava mais 4 a 8 s de tela. Com a
  // chave, catálogo que chega sem mudar a query não dispara nada;
  // catálogo que RESOLVE uma dimensão selecionada (o filtro salvo por
  // tema) muda a chave e dispara, como deve. `loadRef` dá ao efeito a
  // versão corrente de `load` sem torná-la dependência.
  const loadRef = useRef(load);
  loadRef.current = load;
  // `waitingForCatalog` (o booleano derivado que segura a consulta
  // enquanto o catálogo não chegou) vem do hook — ver
  // `use-mention-filters.ts`.
  useEffect(() => {
    // Não consulta com o recorte pela metade. Sem esta guarda, o filtro
    // salvo com dimensão de catálogo dispararia uma busca SEM o recorte
    // (resultado errado, com o chip na tela) e só depois outra com ele —
    // e a primeira é a que a pessoa vê.
    if (waitingForCatalog) return;
    void loadRef.current();
  }, [queryKey, sourceMode, token, waitingForCatalog]);

  // Por SELETOR e não por `ref`: o menu de ações se repete por linha da
  // lista, então não há um elemento único a apontar.
  useDismiss(actionMenuId !== null, () => setActionMenuId(null), {
    within: ".mention-actions"
  });

  // "Sem classificação" não é uma polaridade: é o balde das menções que
  // ainda vão ser classificadas. Relatório é foto do que já foi
  // decidido, então esse pedaço do recorte não viaja.
  const semClassificacao = facet("polarities").values.has(NO_VALUE);
  const soSemClassificacao = onlyUnclassified(facet("polarities"));

  // A lista colada sozinha, sem busca nem outro filtro por cima — é
  // quando "N menções em M publicações" é uma frase verdadeira.
  const pastedListIsTheOnlyFilter =
    refFilter !== null &&
    activeFilterCount === 1 &&
    !debouncedQuery &&
    !filters.onlyDiscarded;

  /**
   * O que este recorte tem e o relatório NÃO leva.
   *
   * Desde 2026-08-28 a lista encolheu pra DUAS entradas, e é a medida
   * do que a entrega fez: as doze dimensões, a multi-seleção e o modo
   * Excluir atravessam inteiros. Sobraram as duas que a dona decidiu
   * manter fora, e por razão de negócio — não por limitação:
   *
   * - a nota mínima, porque a rede só ganha nota DURANTE a geração
   *   (filtrar por ela antes deixaria a rede inteira de fora);
   * - "sem classificação", porque é estado transitório de triagem, e
   *   congelar numa foto menções que ainda vão ser classificadas
   *   contradiz o que o relatório é.
   *
   * A tela continua listando em vez de silenciar: gerar um relatório
   * mais largo do que a tela mostra é a classe de bug que esta entrega
   * combate.
   */
  const naoViajam = (): string[] => {
    const fora: string[] = [];
    if (showsCena && filters.minCena.trim() !== "") {
      fora.push(
        "a nota mínima (a rede só ganha nota durante a geração da amostra)",
      );
    }
    if (semClassificacao && !soSemClassificacao) {
      fora.push(
        '"sem classificação" (a amostra congela o que já foi decidido)',
      );
    }
    if (filters.facets.editors.values.size) {
      fora.push(
        '"editado por" (quem corrigiu é atributo da trilha, não da menção)',
      );
    }
    if (filters.onlyDiscarded) {
      fora.push('"só descartadas" (menção descartada nunca entra em amostra)');
    }
    return fora;
  };

  const generateReport = () => {
    setClosingSample(false);
    onGenerateReport?.(reportRecorte());
  };

  // Os chips de upload e da lista colada ficam de fora do que se salva:
  // vêm do link que trouxe a pessoa até aqui ou de uma colagem de agora,
  // e "Upload #7" (ou "80 links") não diz nada pro colega que abrir o
  // filtro amanhã.
  const savableChips = activeChips
    .filter((chip) => !chip.key.startsWith("upload-") && chip.key !== "refs")
    .map((chip) => chip.label);


  // Só upload que gerou menção vira opção: escolher um que rendeu zero
  // devolveria lista vazia e pareceria filtro quebrado. O que já está
  // selecionado entra de qualquer jeito, senão o select ficaria sem a
  // própria opção ativa quando chegasse por link.
  const uploadOptions = useMemo(
    () =>
      uploads.filter(
        (item) => item.mention_count > 0 || uploadFilter.includes(item.id),
      ),
    [uploads, uploadFilter],
  );

  // No modo publicações não existe total (contar custaria ~45% da
  // tela), então o rodapé recebe "quantas vieram + tem próxima?" em vez
  // do total — ver `PagerCount` em `shared/components/Pager.tsx`.
  const pagerCount =
    sourceMode === "brand"
      ? total === null
        ? { hasNext: mentions.length === pageSize, countOnPage: mentions.length }
        : { total }
      : { hasNext, countOnPage: publications.length };

  return (
    <>
      <div
        className={`mentions-page-body ${filtersOpen ? "with-filters-panel" : ""}`}
      >
        {/* Duas linhas, sempre (2026-10-30, pedido da dona): os filtros
            em cima — os rápidos que a pessoa escolheu, mais "Mais
            filtros" e os salvos — e as AÇÕES sobre o recorte embaixo,
            separadas. Com muitos filtros rápidos a linha de cima quebra
            em quantas precisar; as ações nunca se misturam a ela. */}
        <section className="filter-panel mention-toolbar">
          <div className="mention-toolbar-filters" role="group" aria-label="Filtros">
            <MentionQuickFilters
              hook={filtersHook}
              keys={quickFilters}
              onChangeKeys={changeQuickFilters}
              showsCena={showsCena}
              searchError={searchError}
              uploadOptions={uploadOptions}
              uploadFilter={uploadFilter}
              onChangeUploadFilter={onChangeUploadFilter}
            />
            <button
              className={`secondary-button ${filtersOpen ? "active" : ""}`}
              aria-pressed={filtersOpen}
              onClick={() => (filtersOpen ? closePanel() : openPanel())}
            >
              <Filter size={16} />
              Mais filtros
              {activeFilterCount > 0 && (
                <span className="filter-count">{activeFilterCount}</span>
              )}
            </button>
            {!readOnly && <SavedFiltersButton onClick={() => setLoadingFilter(true)} />}
          </div>
          <div className="mention-toolbar-actions" role="group" aria-label="Ações do recorte">
          {onGenerateReport && !readOnly && (
            <button
              type="button"
              className="secondary-button"
              disabled={soSemClassificacao}
              title={
                soSemClassificacao
                  ? "Menções sem classificação ainda vão ser classificadas — uma amostra congela o que já foi decidido, então esse recorte não gera amostra."
                  : undefined
              }
              onClick={() => setClosingSample(true)}
            >
              <FileBarChart size={16} />
              Fechar amostra
            </button>
          )}
          {/* Ponto de encaixe do Versa (vazio com ele desligado). Gera um
              relatório com o recorte — as mesmas travas do botão acima. */}
          {!readOnly && (
            <SendToVersaButton
              token={token}
              buildQuery={buildQuery}
              buildRecorte={reportRecorte}
              notInReport={naoViajam}
              disabledReason={
                soSemClassificacao
                  ? "Menções sem classificação ainda vão ser classificadas — uma amostra congela o que já foi decidido, então esse recorte não gera amostra."
                  : null
              }
            />
          )}
          {/* Fecha a amostra com o recorte e abre o dashboard nela — as
              mesmas travas do "Fechar amostra". */}
          {onOpenDashboardDraft && !readOnly && (
            <SendToDashboardButton
              token={token}
              buildQuery={buildQuery}
              buildRecorte={reportRecorte}
              notInReport={naoViajam}
              onOpenDraft={onOpenDashboardDraft}
              disabledReason={
                soSemClassificacao
                  ? "Menções sem classificação ainda vão ser classificadas — uma amostra congela o que já foi decidido, então esse recorte não gera amostra."
                  : null
              }
            />
          )}
          </div>
        </section>
        {activeBulk && (
          <BulkEditBanner
            edit={activeBulk}
            failures={bulkFailures}
            onShowFailures={() => void showBulkFailures()}
            onDismiss={() => {
              setActiveBulk(null);
              setBulkFailures(null);
            }}
          />
        )}
        {activeReprocess && (
          <AiReprocessBanner
            run={activeReprocess}
            issues={reprocessIssues}
            onShowIssues={() => void showReprocessIssues()}
            onDismiss={() => {
              setActiveReprocess(null);
              setReprocessIssues(null);
            }}
            onOpenDetail={
              onOpenReprocessRun
                ? () => onOpenReprocessRun(activeReprocess.id)
                : undefined
            }
          />
        )}
        {activeRecalc && (
          <CenaRecalcBanner
            token={token}
            run={activeRecalc}
            onSettled={() => void load({ silent: true })}
            onUndone={(next) => setActiveRecalc(next)}
            onDismiss={() => setActiveRecalc(null)}
          />
        )}
        {!readOnly && (selectionCount === null || selectionCount > 0) && sourceMode === "brand" && (
          <div className="bulk-action-bar" role="region" aria-label="Seleção">
            <ListChecks size={16} aria-hidden="true" />
            <strong>
              {selectionCount === null ? (
                "Todas as menções do filtro selecionadas"
              ) : (
                <>
                  {selectionCount.toLocaleString("pt-BR")}{" "}
                  {selectionCount === 1 ? "menção selecionada" : "menções selecionadas"}
                  {selectAllFiltered && " (todas do filtro)"}
                </>
              )}
            </strong>
            {!selectAllFiltered && (total === null || total > mentions.length) && (
              <button
                type="button"
                className="text-button"
                onClick={() => setSelectAllFiltered(true)}
              >
                {total === null
                  ? "Selecionar todas do filtro"
                  : `Selecionar todas as ${total.toLocaleString("pt-BR")} do filtro`}
              </button>
            )}
            <span className="bulk-action-spacer" />
            {filters.onlyDiscarded ? (
              <button
                type="button"
                className="primary-button"
                onClick={() => void discard.restoreBulk()}
              >
                <RotateCcw size={15} aria-hidden="true" />
                Restaurar selecionadas
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => setBulkDialogOpen(true)}
                >
                  <SquarePen size={15} aria-hidden="true" />
                  Corrigir em lote
                </button>
                {canReprocess && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setReprocessDialogOpen(true)}
                  >
                    <Sparkles size={15} aria-hidden="true" />
                    Reprocessar com IA
                  </button>
                )}
                {canRecalcCena && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setRecalcDialogOpen(true)}
                  >
                    <Calculator size={15} aria-hidden="true" />
                    Recalcular CENA
                  </button>
                )}
                {/* Vincular vale pra seleção MARCADA, até 100 por vez — o
                    servidor recusa mais. "Todas do filtro" não entra: cada
                    publicação vira uma menção nova, e isso se faz olhando o
                    que se selecionou. */}
                {!selectAllFiltered && (
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={selected.size > LINK_MAX_SOURCES}
                    title={
                      selected.size > LINK_MAX_SOURCES
                        ? `No máximo ${LINK_MAX_SOURCES} menções por vez.`
                        : undefined
                    }
                    onClick={() => setLinkTarget({ mentionIds: [...selected] })}
                  >
                    <Link2 size={15} aria-hidden="true" />
                    Vincular a outra marca
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-button is-danger"
                  onClick={() => discard.requestBulkDiscard(selectionCount)}
                >
                  <Trash2 size={15} aria-hidden="true" />
                  Descartar selecionadas
                </button>
              </>
            )}
            <button type="button" className="text-button" onClick={clearSelection}>
              Limpar seleção
            </button>
          </div>
        )}
        {activeChips.length > 0 && (
          <div className="active-filters-bar">
            <span className="active-filters-label">Filtros ativos:</span>
            {activeChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className="active-filter-chip"
                onClick={chip.onRemove}
              >
                {chip.label}
                <X size={12} />
              </button>
            ))}
            <button
              type="button"
              className="text-button"
              onClick={() => setSavingFilter(true)}
              disabled={savableChips.length === 0}
              title={
                savableChips.length === 0
                  ? "O filtro de upload é do link que trouxe você aqui — não entra num filtro salvo."
                  : undefined
              }
            >
              Salvar filtro
            </button>
            <button
              type="button"
              className="text-button"
              onClick={() => {
                void clearFilters();
                setQuery("");
                setSearchIn("all");
              }}
            >
              Limpar tudo
            </button>
          </div>
        )}
        <section className="card mentions-table-card">
          <div className="table-meta">
            {sourceMode === "brand" ? (
              <span className="table-meta-count">
                {!readOnly && <label
                  className="mention-rows-select-all"
                  title="Selecionar as menções desta página"
                >
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={togglePage}
                    aria-label="Selecionar as menções desta página"
                  />
                </label>}
                {total === null ? (
                  <em>
                    {totalError
                      ? "Total indisponível"
                      : totalGaveUp
                        ? "Muitas menções: a busca casa mais do que dá pra contar em 2 s"
                        : "Calculando o total…"}
                  </em>
                ) : (
                  <>
                    <strong>{total}</strong> {total === 1 ? "menção" : "menções"}
                  </>
                )}
                {/* Com lista colada os dois números andam lado a lado e
                    confundem: a lista acha PUBLICAÇÕES, e cada uma vira
                    uma menção por marca. "em N publicações" só é verdade
                    quando a lista é o único filtro — com outro por cima,
                    parte das publicações dela pode ter ficado de fora. */}
                {refFilter &&
                  (pastedListIsTheOnlyFilter
                    ? ` em ${refFilter.postCount} ${
                        refFilter.postCount === 1 ? "publicação" : "publicações"
                      }`
                    : ` · lista colada: ${refFilter.postCount} ${
                        refFilter.postCount === 1 ? "publicação" : "publicações"
                      }`)}
              </span>
            ) : (
              /* Sem contagem: no modo publicações o servidor manda
               `has_next` em vez de `total` — contar exigiria agrupar
               todas as menções da empresa. */
              <span>Publicações</span>
            )}
            <div className="table-meta-controls">
              {/* A ordem vale pras duas visões — é o mesmo recorte lido
                de dois jeitos, e uma ordem que só existisse numa seria
                armadilha ao trocar. Lista fechada e curta (regra da
                `SearchSelect`), desenhada pela `ToolbarSelect` em vez do
                menu nativo do navegador (2026-10-13). */}
              {/* O período da lista (2026-10-26): os mesmos 1, 7, 15 e 30
                  dias da Visão geral, contados até o último dado, mais
                  "Personalizado", que abre o painel pra pessoa digitar as
                  datas — e é o botão aceso quando o período de agora não
                  casa nenhum dos quatro (ou não há período). Não há "tudo":
                  ver a empresa inteira é esvaziar as datas no painel, de
                  propósito. Clicar num período grava a janela no filtro
                  compartilhado e a escolha, que Início e Visão geral
                  também leem. */}
              <SummaryPeriodPicker
                days={activePeriod}
                onChange={(days) => void choosePeriod(days)}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={activePeriod === null}
                  className={activePeriod === null ? "active" : ""}
                  onClick={openPanel}
                >
                  Personalizado
                </button>
              </SummaryPeriodPicker>
              {hasCompetitors && (
                <ToolbarSelect<MentionListScope>
                  prefix="Menções"
                  icon={Building2}
                  ariaLabel="De quem são as menções da lista"
                  value={scope}
                  onChange={changeScope}
                  options={[
                    { value: "own", label: "A empresa" },
                    { value: "competitors", label: "Concorrentes" },
                    { value: "all", label: "Todas" },
                  ]}
                />
              )}
              <ToolbarSelect<MentionOrder>
                prefix="Ordem"
                icon={ArrowUpDown}
                ariaLabel="Ordem da lista"
                value={order}
                onChange={changeOrder}
                options={MENTION_ORDERS.filter(
                  (option) => filters.onlyDiscarded || !TRASH_ORDERS.includes(option),
                ).map((option) => ({
                  value: option,
                  label: MENTION_ORDER_LABELS[option],
                }))}
              />
              <ToolbarSelect<MentionTextView>
                prefix="Texto"
                icon={FileText}
                ariaLabel="O que mostrar como texto da menção"
                value={textView}
                onChange={(next) => void changeTextView(next)}
                options={MENTION_TEXT_VIEWS.map((option) => ({
                  value: option,
                  label: MENTION_TEXT_VIEW_LABELS[option],
                }))}
              />
              {/* Lista e Revisão são dois jeitos de ler as menções; o
                  "Publicações" (uma linha por publicação, juntando as
                  marcas) fica FORA do seletor, num botão ao lado
                  (2026-10-15): é outro recorte, não outro jeito de ver. */}
              {!readOnly && <ToolbarSelect<ReadingMode>
                prefix="Ver"
                ariaLabel="Como ver as menções"
                align="right"
                value={readingMode}
                onChange={(next) => {
                  changeSourceMode("brand");
                  setReadingMode(next);
                }}
                options={[
                  { value: "list", label: "Lista", icon: ListIcon },
                  { value: "review", label: "Revisão", icon: PanelsTopLeft },
                ]}
              />}
              <button
                type="button"
                className={`toolbar-toggle${sourceMode === "publication" ? " is-active" : ""}`}
                aria-pressed={sourceMode === "publication"}
                onClick={() => changeSourceMode(sourceMode === "publication" ? "brand" : "publication")}
              >
                <Newspaper size={15} aria-hidden="true" />
                Publicações
              </button>
              <button
                type="button"
                className="table-refresh-button is-icon mention-data-tooltip"
                onClick={() => void load()}
                aria-label="Atualizar"
                data-tooltip="Atualizar"
                data-tooltip-position="bottom"
              >
                <RefreshCw size={15} />
              </button>
            </div>
          </div>
          {/* Primeira carga da lista por marca: cards-esqueleto no formato
              do card, pra tela não "pular" quando os dados chegam. Recarga
              com a lista na tela continua com o aviso de sempre. */}
          {loading && sourceMode === "brand" && mentions.length === 0 ? (
            <div className="mention-skeletons" role="status">
              <span className="sr-only">Carregando menções…</span>
              {[0, 1, 2].map((index) => (
                <div className="mention-skeleton" key={index} aria-hidden="true">
                  <div className="mention-skeleton-main">
                    <span className="sk sk-line sk-short" />
                    <span className="sk sk-line sk-title" />
                    <span className="sk sk-line" />
                    <span className="sk sk-line sk-mid" />
                  </div>
                  <div className="mention-skeleton-side">
                    <span className="sk sk-pill" />
                    <span className="sk sk-line sk-mid" />
                    <span className="sk sk-line" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            loading && <ApiState kind="loading">Carregando menções…</ApiState>
          )}
          {error && (
            <ApiState kind="error" onRetry={() => void load()}>
              {error}
            </ApiState>
          )}
          <div className="mentions-grid-scroll">
            {sourceMode === "publication" && (
              <>
                <div className="mentions-list" role="list">
                  {publications.map((publication) => {
                    const tier = influencerTier(publication.followerCount);
                    const platform = mentionPlatform(publication);
                    const PlatformIcon = platform.Icon;
                    const metrics = engagementMetrics(publication);
                    const untitled = publication.title === "Publicação sem título";
                    const isPress = publication.mediaType === "press";
                    return (
                      <article
                        className="mention-row publication-row"
                        key={publication.rawPostId}
                        role="listitem"
                      >
                        <div className="mention-row-grid">
                          <div className="mention-row-main">
                            <div className="mention-row-origin">
                              <span className={`mention-row-logo net-${platform.tone}`} title={platform.label}>
                                {hasPlatformLogo(platform.tone) ? (
                                  <PlatformLogo tone={platform.tone} size={14} mono/>
                                ) : (
                                  <PlatformIcon size={15} aria-hidden="true" />
                                )}
                              </span>
                              <span className="mention-row-source">{publication.source}{publisherSuffix(publication)}</span>
                              <span className="mention-row-date">{publication.date}</span>
                            </div>
                            {/* Sem `onOpen`: aqui o título não abre nada —
                                quem abre é cada marca citada (classificação
                                e CENA são por marca). */}
                            {!untitled && (
                              <div className="mention-row-title">
                                <PublicationTitle title={publication.title} />
                              </div>
                            )}
                            <p className={untitled ? "mention-row-excerpt is-lead" : "mention-row-excerpt"}>
                              {publication.excerpt}
                            </p>
                            <div className="mention-row-foot">
                              <PublicationTags
                                theme={publication.theme}
                                code={publication.nativeId}
                                mediaType={publication.mediaType}
                                link={publication.link}
                                source={`${publication.source}${publisherSuffix(publication)}`}
                                className="mention-row-tags"
                                showOriginFallback={false}
                              />
                            </div>
                          </div>

                          <aside className="mention-row-side">
                            <div className="publication-row-brands">
                              <span className="mention-row-reach-caption">
                                {publication.brands.length === 1 ? "Marca citada" : `${publication.brands.length} marcas citadas`}
                              </span>
                              <div className="publication-row-brand-list">
                                {publication.brands.map((brand) => (
                                  <button
                                    type="button"
                                    className="publication-row-brand"
                                    key={brand.mentionId}
                                    onClick={() => void openBrandMention(brand.mentionId)}
                                    aria-label={`Abrir menção de ${brand.brandName}`}
                                    title={`Abrir a menção de ${brand.brandName}`}
                                  >
                                    {brand.brandName}
                                    {brand.isCompetitor && (
                                      <span className="publication-brand-competitor">concorrente</span>
                                    )}
                                    <ChevronRight size={13} aria-hidden="true" />
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="mention-row-reach">
                              <span className="mention-row-reach-caption">
                                {isPress ? "Valoração" : "Alcance"}
                              </span>
                              <div className="mention-row-reach-value">
                                <ReachOrValue
                                  mediaType={publication.mediaType}
                                  reach={publication.reach}
                                  adValue={publication.adValue}
                                  size={15}
                                />
                                {tier && <span className="mention-row-tier">{tier}</span>}
                              </div>
                            </div>
                            {metrics.length > 0 ? (
                              <div className="mention-row-engagement">
                                <EngagementMetricsList
                                  metrics={metrics}
                                  chipClassName="mention-row-metric"
                                  iconSize={12}
                                />
                              </div>
                            ) : (
                              <PressReachLine
                                pressReach={isPress ? publication.pressReach : null}
                                className="mention-row-no-metrics"
                                fallback={
                                  isPress
                                    ? "Sem métricas de engajamento (imprensa)"
                                    : "Sem métricas de engajamento"
                                }
                              />
                            )}
                          </aside>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            )}
            {sourceMode === "brand" && (
              <>
                {reviewMode ? (
                  <MentionReviewMode
                    companyLogoUrl={companyLogoUrl}
                    textView={textView}
                    mentions={mentions}
                    token={token}
                    onReload={(mentionId) => void refreshMentionInPlace(mentionId)}
                    loadCatalog={loadCatalog}
                    onToggleReviewed={toggleReviewed}
                    onToggleDiscard={discard.requestDiscard}
                    onLink={(mention) =>
                      setLinkTarget({
                        mentionIds: [mention.id],
                        sourceBrandId: mention.brandId,
                        sources: [mention.mediaType as PostSource],
                      })
                    }
                    onError={(message) => setToast({ message })}
                    actionMenuId={actionMenuId}
                    onToggleActionMenu={(id) =>
                      setActionMenuId((current) => (current === id ? null : id))
                    }
                    onCloseActionMenu={() => setActionMenuId(null)}
                    page={page}
                    pageSize={pageSize}
                    pageSizes={MENTION_PAGE_SIZES}
                    onPageSize={setPageSize}
                    // Com busca de texto o total chega numa segunda requisição
                    // (release, 2026-10-15): enquanto não chega, o modo revisão
                    // anda pela página cheia, como o rodapé de publicações.
                    pageCount={total === null ? page + (mentions.length === pageSize ? 1 : 0) : Math.max(1, Math.ceil(total / pageSize))}
                    total={total ?? (page - 1) * pageSize + mentions.length}
                    canPrevPage={page > 1}
                    canNextPage={total === null ? mentions.length === pageSize : page * pageSize < total}
                    onPrevPage={() => setPage(page - 1)}
                    onNextPage={() => setPage(page + 1)}
                  />
                ) : (
                <div
                  className="mentions-list"
                  role="list"
                >
                  {mentions.map((mention) => (
                    <MentionCard
                      key={mention.id}
                      mention={mention}
                      selected={selectAllFiltered || selected.has(mention.id)}
                      expanded={expandedIds.has(mention.id)}
                      token={token}
                      companyLogoUrl={companyLogoUrl}
                      onToggleSelect={() => toggleSelect(mention.id)}
                      onToggleExpanded={() => toggleExpanded(mention.id)}
                      onOpen={() => openMention(mention)}
                      onToggleReviewed={() => void toggleReviewed(mention)}
                      onLink={() =>
                        setLinkTarget({
                          mentionIds: [mention.id],
                          sourceBrandId: mention.brandId,
                          sources: [mention.mediaType as PostSource],
                        })
                      }
                      onDiscard={() => discard.requestDiscard(mention)}
                      onSaved={() => void refreshMentionInPlace(mention.id)}
                      onError={(message) => setToast({ message })}
                      loadCatalog={loadCatalog}
                      readOnly={readOnly}
                      textView={textView}
                    />
                  ))}

                </div>
                )}
              </>
            )}
          </div>
          {!loading &&
            !error &&
            (sourceMode === "brand"
              ? mentions.length === 0
              : publications.length === 0) && (
              <div className="empty-state">
                <Search size={30} />
                <strong>
                  {sourceMode === "brand"
                    ? "Nenhuma menção encontrada"
                    : "Nenhuma publicação encontrada"}
                </strong>
                <span>
                  {activeFilterCount > 0 || query
                    ? "Tente remover alguns filtros ou buscar outro termo."
                    : "As menções aparecem aqui depois que um upload é processado pela esteira."}
                </span>
              </div>
            )}
          {/* No Modo revisão a paginação mora no pé da fila. */}
          {!(reviewMode && sourceMode === "brand") && (
            <Pager
              page={page}
              size={pageSize}
              count={pagerCount}
              onPage={setPage}
              sizes={MENTION_PAGE_SIZES}
              onSize={setPageSize}
              busy={loading}
            />
          )}
        </section>
      </div>

      {filtersOpen && (
        <MentionFilterPanel
          hook={filtersHook}
          mode="mentions"
          showsCena={showsCena}
          eyebrow="MENÇÕES"
          uploadOptions={uploadOptions}
          uploadFilter={uploadFilter}
          onChangeUploadFilter={onChangeUploadFilter}
          pins={{
            pinned: quickFilters,
            onToggle: (key) => changeQuickFilters(togglePinnedQuickFilter(quickFilters, key)),
            onRestoreDefault: () => changeQuickFilters([...DEFAULT_QUICK_FILTERS]),
            isDefault:
              quickFilters.length === DEFAULT_QUICK_FILTERS.length &&
              quickFilters.every((key, index) => key === DEFAULT_QUICK_FILTERS[index]),
          }}
        />
      )}

      {discard.pending && (
        <ConfirmDialog
          {...discardDialogCopy(discard.pending)}
          confirmLabel="Descartar"
          danger
          loading={discard.busy}
          error={discard.error}
          onConfirm={() => void discard.confirm()}
          onCancel={discard.cancel}
        />
      )}

      {bulkDialogOpen && (
        <BulkClassificationDialog
          count={selectionCount}
          targetLabel={
            selectAllFiltered
              ? "Todas as menções que o filtro atual devolve — o servidor congela a lista no clique."
              : "As menções marcadas na lista."
          }
          loadTarget={() => apiClient.bulkTarget(token, bulkTarget())}
          onSubmit={submitBulk}
          onClose={() => setBulkDialogOpen(false)}
        />
      )}
      {reprocessDialogOpen && (
        <AiReprocessDialog
          token={token}
          count={selectionCount}
          targetLabel={
            selectAllFiltered
              ? "Todas as menções que o filtro atual devolve — o servidor congela a lista no clique."
              : "As menções marcadas na lista."
          }
          target={bulkTarget}
          onApplied={(run) => {
            setReprocessDialogOpen(false);
            clearSelection();
            setReprocessIssues(null);
            setActiveReprocess(run);
          }}
          onClose={() => setReprocessDialogOpen(false)}
        />
      )}
      {recalcDialogOpen && (
        <CenaRecalcDialog
          token={token}
          count={selectionCount}
          targetLabel={
            selectAllFiltered
              ? "Todas as menções que o filtro atual devolve — o servidor congela a lista no clique."
              : "As menções marcadas na lista."
          }
          target={bulkTarget}
          onApplied={(run) => {
            setRecalcDialogOpen(false);
            clearSelection();
            setActiveRecalc(run);
          }}
          onClose={() => setRecalcDialogOpen(false)}
        />
      )}
      {reopenedSample?.target && (
        <AiReprocessDialog
          token={token}
          // O tamanho do recorte só se sabe na estimativa; o da prévia
          // é o da AMOSTRA, e mostrá-lo no título enganaria.
          count={null}
          targetLabel="O mesmo recorte da prévia — o servidor congela a lista de novo no clique."
          target={() => ({
            savedQuery: reopenedSample.target!.query,
            savedMentionIds: reopenedSample.target!.mention_ids
          })}
          initialSample={reopenedSample}
          onApplied={(run) => {
            setReopenedSample(null);
            setReprocessIssues(null);
            setActiveReprocess(run);
          }}
          onClose={() => setReopenedSample(null)}
        />
      )}
      {linkTarget && (
        <LinkMentionDialog
          token={token}
          mentionIds={linkTarget.mentionIds}
          sourceBrandId={linkTarget.sourceBrandId}
          loadSources={() =>
            linkTarget.sources
              ? Promise.resolve(linkTarget.sources)
              : apiClient
                  .bulkTarget(
                    token,
                    selectionTarget({
                      selectAllFiltered: false,
                      selected: new Set(linkTarget.mentionIds),
                      scope,
                      buildQuery,
                    })
                  )
                  .then((found) => found.post_sources)
          }
          onClose={() => setLinkTarget(null)}
          onDone={message => {
            setLinkTarget(null);
            clearSelection();
            setToast({ message });
            void load();
          }}
        />
      )}
      {toast && (
        <Toast
          message={toast.message}
          actionLabel={toast.undo ? "Desfazer" : undefined}
          onAction={toast.undo}
          onClose={() => setToast(null)}
        />
      )}
      {/* Irmãos de topo, fora do drawer de filtros: `.filters-panel`
          tem `overflow: hidden` e cortaria qualquer diálogo montado
          dentro dele. */}
      {savingFilter && (
        <SaveFilterDialog
          token={token}
          criteria={criteriaFromState({ query, queryIn: searchIn, filters }, resolveIds)}
          chips={savableChips}
          onSaved={() => setSavingFilter(false)}
          onClose={() => setSavingFilter(false)}
        />
      )}

      {loadingFilter && (
        <LoadFilterDialog
          token={token}
          onApply={(filter) => {
            applySavedFilter(filter.criteria);
            setLoadingFilter(false);
          }}
          onClose={() => setLoadingFilter(false)}
        />
      )}

      {closingSample && (
        <ConfirmDialog
          title="Fechar amostra"
          icon={FileBarChart}
          description={`${total === null
            ? "O total do filtro ainda está sendo calculado."
            : `${total === 1 ? "Entra" : "Entram"} ${total === 1 ? "1 menção" : `as ${total.toLocaleString("pt-BR")} menções`} do filtro atual.`
          } O filtro atual vira uma amostra (rascunho, vence em 7 dias) e a tela da amostra abre já calculando. O que você vê aqui continua como está.`}
          details={{ title: "Fica de fora", items: naoViajam() }}
          confirmLabel="Fechar amostra"
          onConfirm={generateReport}
          onCancel={() => setClosingSample(false)}
        />
      )}
    </>
  );
}

